import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { App } from '@capacitor/app';
import { routeUitDeepLink } from '../utils/deepLink';

/**
 * Opent een aangetikte clubcoachapp.nl-link in de app zelf.
 *
 * Krijgt iemand een opstelling in de groepsapp en heeft hij de app staan, dan hoort
 * hij in de app te belanden en niet in Safari. iOS regelt de eerste helft daarvan
 * (Universal Links, zie public/.well-known/apple-app-site-association) en geeft de
 * URL door aan de webview; hier zetten we hem om in een navigatie.
 *
 * Rendert niets en hoort binnen de Router te staan, want daarbuiten bestaat
 * useNavigate niet. Op web vuurt appUrlOpen niet, dus daar is dit een no-op.
 */
export const DeepLinks: React.FC = () => {
  const navigate = useNavigate();

  // Via een ref, zodat een nieuwe navigate-referentie de listener niet opnieuw
  // aanmeldt. Meldt hij zich opnieuw aan, dan is het luisterhaakje even weg - precies
  // het moment waarop een koud gestarte app zijn link binnenkrijgt.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    let active = true;
    let removeListener: (() => void) | undefined;

    // Capacitor bewaart dit event tot er een luisteraar is (retainUntilConsumed), dus
    // een link die de app koud opstart komt alsnog binnen zodra React staat.
    App.addListener('appUrlOpen', ({ url }) => {
      if (!active) return;

      const route = routeUitDeepLink(url);
      if (route) navigateRef.current(route);
    })
      .then((handle) => {
        if (active) {
          removeListener = () => handle.remove();
        } else {
          handle.remove();
        }
      })
      .catch(() => {
        // Geen plugin (web of tests): dan is er niets om op te wachten.
      });

    return () => {
      active = false;
      removeListener?.();
    };
  }, []);

  return null;
};
